/**
 * Which browser is this, and what should the install button say?
 *
 * The extension is published once, on the Chrome Web Store, and every
 * Chromium-based browser installs from there — so the *destination* never
 * changes and only the label does. "Add to Brave" on Brave is the difference
 * between a button someone recognises as being for them and one they assume is
 * for a browser they don't use.
 *
 * Detection has to run in the browser, and the page is prerendered: the server
 * renders the `chrome` default, and the label is corrected on mount. The CTA
 * reserves a fixed width for exactly that reason (see ExtensionInstallCta) —
 * a label that grows from "Add to Edge" to "Add to Vivaldi" must not move the
 * paragraph under it.
 */
import { onMounted, ref } from 'vue'

export type BrowserId = 'chrome' | 'brave' | 'edge' | 'opera' | 'vivaldi' | 'arc' | 'firefox' | 'safari'

export interface DetectedBrowser {
  id: BrowserId
  /** Display name, as the vendor writes it. */
  name: string
  /** Can it install from the Chrome Web Store? */
  chromium: boolean
}

const CHROME: DetectedBrowser = { id: 'chrome', name: 'Chrome', chromium: true }

/**
 * Read the browser out of the user agent (plus the two vendor-specific probes
 * that a UA string can't answer).
 *
 * Order matters: every Chromium fork keeps "Chrome" in its UA, so the forks are
 * tested first and Chrome is what's left. Safari has to come last for the same
 * reason in the other direction — Chrome's UA contains "Safari" too.
 */
function sniff(): DetectedBrowser {
  const ua = navigator.userAgent

  // Vivaldi and Opera brand their UA; Edge uses "Edg/" (no "e") on every
  // platform since it moved to Chromium.
  if (/\bVivaldi\//.test(ua)) return { id: 'vivaldi', name: 'Vivaldi', chromium: true }
  if (/\bOPR\//.test(ua) || /\bOpera\b/.test(ua)) return { id: 'opera', name: 'Opera', chromium: true }
  if (/\bEdg(?:|A|iOS)\//.test(ua)) return { id: 'edge', name: 'Edge', chromium: true }

  // Arc ships a stock Chrome user agent, so the only tell from a page is the
  // theme variable it defines on the document element.
  try {
    const arc = getComputedStyle(document.documentElement).getPropertyValue('--arc-palette-title')
    if (arc.trim() !== '') return { id: 'arc', name: 'Arc', chromium: true }
  } catch {
    // getComputedStyle can't fail in practice, but this runs before anything is
    // painted and a wrong answer here is worth less than a broken page.
  }

  if (/\bFirefox\//.test(ua)) return { id: 'firefox', name: 'Firefox', chromium: false }

  // Chrome's own UA ends in "Safari/537.36", so Safari is only what is left once
  // Chromium has been ruled out. `userAgentData` is a Chromium-only API and is
  // the reliable half of that test; the token match catches the builds that
  // don't expose it and the ones that prefix it (`HeadlessChrome/`), which is
  // why there is no word boundary in front of it.
  const chromium = Boolean((navigator as any).userAgentData) || /Chrom(?:e|ium)\//.test(ua)
  // iOS reports every browser as Safari, which is true enough for this purpose:
  // none of them can install a Chrome extension.
  if (!chromium && /Safari\//.test(ua)) return { id: 'safari', name: 'Safari', chromium: false }

  return CHROME
}

/**
 * The detected browser, `chrome` until the client has had a chance to look.
 *
 * Brave is the one that can't be sniffed synchronously: it deliberately ships
 * Chrome's user agent and exposes `navigator.brave.isBrave()`, which is a
 * promise. So the ref settles twice on Brave — Chrome first, then Brave a
 * microtask later — which is invisible next to the mount itself.
 */
export function useBrowserDetect() {
  const browser = ref<DetectedBrowser>(CHROME)
  /** False until detection has run, so a caller can hold back UA-specific copy. */
  const detected = ref(false)

  onMounted(async () => {
    browser.value = sniff()
    detected.value = true

    // Only worth asking when the UA looked like plain Chrome — Brave is the
    // only browser that hides behind it.
    if (browser.value.id !== 'chrome') return
    try {
      const brave = (navigator as any).brave
      if (brave?.isBrave && (await brave.isBrave())) {
        browser.value = { id: 'brave', name: 'Brave', chromium: true }
      }
    } catch {
      // Not Brave, or Brave changed its mind about the API. Chrome stands.
    }
  })

  return { browser, detected }
}
